/* global Office console File FormData Uint8Array */

import dashboardNetwork from "../services/network/dashboard.network";
import { DocumentUploadResBody } from "../types/dashboard.types";

const CHUNK_SIZE = 1024 * 1024 * 4; // 4MB chunks

const OFFICE_MIME_TYPES = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  pdf: "application/pdf",
} as const;

export async function uploadFile(): Promise<DocumentUploadResBody> {
  try {
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(Office.FileType.Compressed, async (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }

        const file: Office.File = result.value;
        const slices: number = Math.ceil(file.size / CHUNK_SIZE);
        const chunks: Uint8Array[] = [];

        try {
          const filename = Office.context.document.url.split("/").pop() ?? "document.pdf";
          const mimeType = getMimeType(filename);

          // Collect all slices first
          for (let i = 0; i < slices; i++) {
            const slice = await new Promise<Office.Slice>((resolve, reject) => {
              file.getSliceAsync(i, (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                  reject(new Error(asyncResult.error.message));
                } else {
                  resolve(asyncResult.value);
                }
              });
            });

            // Convert slice data to Uint8Array
            const buffer = new Uint8Array(slice.data);
            chunks.push(buffer);
          }

          // Combine all chunks
          const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
          const combinedBuffer = new Uint8Array(totalLength);

          let offset = 0;
          for (const chunk of chunks) {
            combinedBuffer.set(chunk, offset);
            offset += chunk.length;
          }

          // Create file from combined buffer
          const combinedFile = new File([combinedBuffer], filename, { type: mimeType });

          const formData = new FormData();
          formData.append("documents", combinedFile);

          const res = await dashboardNetwork.uploadFile(formData, filename);
          if (res.type === "failure") return reject(new Error(res.message));

          file.closeAsync();
          resolve(res.data);
        } catch (error) {
          file.closeAsync();
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error("Upload error:", error);
    throw error;
  }
}

function getMimeType(filename: string): string {
  const extension = filename.split(".").pop();
  return OFFICE_MIME_TYPES[extension as keyof typeof OFFICE_MIME_TYPES] ?? "application/octet-stream";
}
